import { router } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  isValidLinkedInProfileUrl,
  normalizeLinkedInProfileUrl,
  normalizeXProfileUrl,
} from '@/lib/validations/social-profiles';
import type { ApplyPhotoStep, ApplyProfileForm } from '@/types';
import { errorMessages } from './errors';
import { EventLabel } from './EventLabel';
import { BuilderStep } from './profile-steps/BuilderStep';
import { ConsiderationsStep } from './profile-steps/ConsiderationsStep';
import { EducationStep } from './profile-steps/EducationStep';
import { PersonalStep } from './profile-steps/PersonalStep';
import { PhotoStep } from './profile-steps/PhotoStep';
import { RoleStep } from './profile-steps/RoleStep';
import {
  type ProfileFormData,
  type ProfileStep,
  profileSteps,
  STEP_LABELS,
} from './profile-steps/types';
import { VeteranStep } from './profile-steps/VeteranStep';
import { useLinkedinCheck } from './use-linkedin-check';

interface Props {
  ownerName: string;
  /** First name (or full name) shown in step headers like "{name}.builder?" */
  ownerFirstName: string;
  initial: ApplyProfileForm;
  /** Where the finished wizard posts. The server redirects on success. */
  submitUrl: string;
  submitMethod: 'post' | 'patch';
  submitLabel?: string;
  /** Rendered as the "← Atrás" target on the first step; absent hides it. */
  cancelHref?: string;
  /**
   * If provided, in-progress data + current step are saved to localStorage
   * under this key and restored on mount. Cleared after a successful submit.
   * Scope it per-form (e.g. include submission id, slot token, or user id).
   */
  storageKey?: string;
  /**
   * Where the LinkedIn check is polled (Apply::LinkedinChecksController). When set, the
   * personal step only completes once the server says the profile exists and is free.
   */
  linkedinCheckUrl?: string | null;
  /**
   * The profile-photo step (Apply::ProfilePhotosController), run right after the personal
   * step. Absent for editions without it.
   */
  photoStep?: ApplyPhotoStep | null;
}

interface StoredDraft {
  v: 1;
  savedAt: number;
  data: ProfileFormData;
  stepIdx: number;
}

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function readDraft(key: string): StoredDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (parsed.v !== 1) return null;
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: StoredDraft) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // quota / privacy mode — silently ignore
  }
}

function clearDraft(key: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

interface StepContext {
  /** null when the edition does not verify LinkedIn, else whether the server confirmed it. */
  linkedinVerified: boolean | null;
  /** The signed ids of the verified photos the photo step is showing right now. */
  photoOptionIds: readonly string[];
}

function isStepComplete(
  step: ProfileStep,
  data: ProfileFormData,
  { linkedinVerified, photoOptionIds }: StepContext,
): boolean {
  switch (step) {
    case 'personal': {
      // Validate the normalized URL so leading/trailing whitespace (common on
      // mobile paste), http://, country subdomains, and trailing slashes don't
      // block an otherwise-valid LinkedIn profile.
      const linkedin = normalizeLinkedInProfileUrl(data.linkedin);
      return (
        data.country.length > 0 &&
        linkedin.length > 0 &&
        isValidLinkedInProfileUrl(linkedin) &&
        linkedinVerified !== false
      );
    }
    case 'builder':
      return data.bio.trim().length >= 20 && data.bio.length <= 850;
    case 'education':
      return data.education.trim().length >= 10;
    case 'role':
      return data.roles.length > 0;
    case 'veteran':
      if (data.isVeteran === null) return false;
      if (data.isVeteran === true)
        return data.previousHackathons.trim().length >= 3;
      return true;
    case 'considerations':
      return data.shirtSize !== '' && data.diet !== '';
    // Only a photo the step is actually offering (verified by the server on this visit)
    // moves on. Skipping goes through the warning modal, which advances by itself.
    case 'photo':
      return (
        data.photoSignedId !== null &&
        photoOptionIds.includes(data.photoSignedId)
      );
  }
}

// The server sends the wizard's starting values already defaulted (ApplyProfileForm),
// which is what the Next page did inline with `existing?.field ?? ''`.
function toFormData(
  initial: ApplyProfileForm,
  photoStep: ApplyPhotoStep | null | undefined,
): ProfileFormData {
  const currentPhoto = photoStep?.current ?? null;
  return {
    photoSignedId: currentPhoto?.signedId ?? null,
    photoUrl: currentPhoto?.url ?? null,
    photoSkipped: currentPhoto === null && (photoStep?.skipped ?? false),
    country: initial.country,
    linkedin: initial.linkedin,
    x: initial.x,
    bio: initial.bio,
    education: initial.education,
    roles: initial.roles,
    isVeteran: initial.isVeteran,
    previousHackathons: initial.previousHackathons,
    shirtSize: initial.shirtSize ?? '',
    diet: initial.diet ?? '',
    allergies: initial.allergies,
    physicalIssues: initial.physicalIssues,
    shareInfoWithSponsors: initial.shareInfoWithSponsors,
  };
}

export function ProfileMultiStep({
  ownerName,
  ownerFirstName,
  initial,
  submitUrl,
  submitMethod,
  submitLabel = 'Enviar',
  cancelHref,
  storageKey,
  linkedinCheckUrl,
  photoStep,
}: Props) {
  const steps = profileSteps(Boolean(photoStep));
  const [data, setData] = useState<ProfileFormData>(() =>
    toFormData(initial, photoStep),
  );
  const linkedinCheck = useLinkedinCheck(linkedinCheckUrl, data.linkedin);
  const linkedinVerified = linkedinCheckUrl
    ? linkedinCheck.status === 'found'
    : null;
  const [stepIdx, setStepIdx] = useState(0);
  const [isPending, setIsPending] = useState(false);
  // Reported by PhotoStep once its candidates load; empty until then.
  const [photoOptionIds, setPhotoOptionIds] = useState<readonly string[]>([]);
  const hydratedRef = useRef(false);

  // Hydrate from localStorage on mount. Done in an effect so the first client
  // render matches the server-rendered markup — state is patched once we know
  // the localStorage value.
  useEffect(() => {
    if (!storageKey || hydratedRef.current) return;
    hydratedRef.current = true;
    const draft = readDraft(storageKey);
    if (!draft) return;
    setData((prev) => ({ ...prev, ...draft.data }));
    if (
      Number.isInteger(draft.stepIdx) &&
      draft.stepIdx >= 0 &&
      draft.stepIdx < steps.length
    ) {
      setStepIdx(draft.stepIdx);
    }
  }, [storageKey, steps.length]);

  // Persist on change (only after hydration completed, so we don't overwrite
  // a freshly-loaded draft with the initial blank state).
  useEffect(() => {
    if (!storageKey) return;
    if (!hydratedRef.current) return;
    writeDraft(storageKey, {
      v: 1,
      savedAt: Date.now(),
      data,
      stepIdx,
    });
  }, [storageKey, data, stepIdx]);

  const currentStep = steps[stepIdx];
  const isFinalStep = stepIdx === steps.length - 1;
  const stepDone = isStepComplete(currentStep, data, {
    linkedinVerified,
    photoOptionIds,
  });
  const progress = (stepIdx + 1) / steps.length;

  const patch = (p: Partial<ProfileFormData>) =>
    setData((prev) => ({ ...prev, ...p }));

  const goNext = () => {
    if (!stepDone) {
      toast.error('Completa los campos requeridos');
      return;
    }
    if (isFinalStep) {
      setIsPending(true);
      router[submitMethod](
        submitUrl,
        {
          bio: data.bio,
          education: data.education,
          linkedin: normalizeLinkedInProfileUrl(data.linkedin),
          x: data.x.trim() ? normalizeXProfileUrl(data.x) : '',
          roles: data.roles,
          isVeteran: data.isVeteran === true,
          previousHackathons:
            data.isVeteran === true ? data.previousHackathons : '',
          shirtSize: data.shirtSize,
          diet: data.diet,
          allergies: data.allergies,
          physicalIssues: data.physicalIssues,
          country: data.country,
          shareInfoWithSponsors: data.shareInfoWithSponsors,
          ...(photoStep
            ? {
                photoSignedId: data.photoSignedId ?? '',
                photoSkipped: data.photoSignedId === null && data.photoSkipped,
              }
            : {}),
        },
        {
          // The wizard's own state is not on the server, so a refused save must
          // not remount the page and throw the answers away.
          preserveState: true,
          onSuccess: () => {
            if (storageKey) clearDraft(storageKey);
          },
          onError: (errors) =>
            toast.error(errorMessages(errors).base ?? 'Datos inválidos'),
          onFinish: () => setIsPending(false),
        },
      );
      return;
    }
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  };

  // Skipping the photo step after the warning modal: recorded, then on to the next step.
  const skipPhoto = () => {
    patch({ photoSkipped: true, photoSignedId: null, photoUrl: null });
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    if (stepIdx === 0) {
      if (cancelHref) router.visit(cancelHref);
      return;
    }
    setStepIdx((i) => Math.max(i - 1, 0));
  };

  return (
    <div className="mx-auto max-w-xl space-y-8 px-4 py-12">
      <header className="space-y-4">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">
            <EventLabel />
          </p>
          <h1 className="font-bold text-xl uppercase tracking-wide sm:text-2xl">
            {ownerName}
          </h1>
        </div>

        <div className="space-y-3">
          <div className="h-1 w-full overflow-hidden rounded-full bg-card">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-lg">
              {STEP_LABELS[currentStep]}
            </div>
            <div className="text-muted-foreground text-xs">
              {stepIdx + 1} / {steps.length}
            </div>
          </div>
        </div>
      </header>

      <div className="border-2 border-primary bg-background/80 p-6">
        {currentStep === 'personal' && (
          <PersonalStep
            ownerName={ownerFirstName}
            data={data}
            onChange={patch}
            linkedinCheck={linkedinCheckUrl ? linkedinCheck : null}
          />
        )}
        {currentStep === 'photo' && photoStep && (
          <PhotoStep
            ownerName={ownerFirstName}
            step={photoStep}
            linkedinUrl={normalizeLinkedInProfileUrl(data.linkedin)}
            data={data}
            onChange={patch}
            onSkip={skipPhoto}
            onOptionsChange={setPhotoOptionIds}
          />
        )}
        {currentStep === 'builder' && (
          <BuilderStep
            ownerName={ownerFirstName}
            data={data}
            onChange={patch}
          />
        )}
        {currentStep === 'education' && (
          <EducationStep
            ownerName={ownerFirstName}
            data={data}
            onChange={patch}
          />
        )}
        {currentStep === 'role' && (
          <RoleStep ownerName={ownerFirstName} data={data} onChange={patch} />
        )}
        {currentStep === 'veteran' && (
          <VeteranStep
            ownerName={ownerFirstName}
            data={data}
            onChange={patch}
          />
        )}
        {currentStep === 'considerations' && (
          <ConsiderationsStep data={data} onChange={patch} />
        )}
      </div>

      <div className="space-y-3 border-border border-t pt-8">
        <div className="flex items-center gap-3">
          {(stepIdx > 0 || cancelHref) && (
            <Button variant="outline" onClick={goBack} disabled={isPending}>
              ← Atrás
            </Button>
          )}
          <div className="flex-1" />
          <Button
            onClick={goNext}
            disabled={isPending || !stepDone}
            className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted"
          >
            {isPending
              ? isFinalStep
                ? 'Enviando...'
                : 'Cargando...'
              : isFinalStep
                ? submitLabel
                : 'Continuar →'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export type { ProfileFormData };
